import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  /** Label for identifying which screen crashed in logs */
  screenName?: string;
  /** Callback to navigate back (e.g., to dashboard) */
  onBack?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Lightweight error boundary for individual screens/components.
 * Unlike the global ErrorBoundary, this shows inline recovery UI
 * so the rest of the app keeps working.
 */
export default class ScreenErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ScreenError:${this.props.screenName || 'unknown'}]`, error, info.componentStack);

    // Stale-chunk recovery — when a deploy bumps the chunk hashes but the
    // user still has an old index.html in the SW / HTTP cache, lazy imports
    // fail with "Failed to fetch dynamically imported module". React catches
    // the rejection BEFORE it can bubble to window, so the global handler
    // in main.tsx never sees it. Detect here and trigger the same recovery.
    const isChunkError = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(error?.message || '');
    if (isChunkError && typeof window !== 'undefined') {
      const RELOAD_FLAG = 'bagrut_chunk_reload';
      const COOLDOWN_MS = 60_000;
      try {
        // Time-based cooldown: allow another recovery after 60s so a kid
        // who clicks two stale lazy routes in a row both auto-recover.
        const raw = sessionStorage.getItem(RELOAD_FLAG);
        const last = raw ? parseInt(raw, 10) : 0;
        const fresh = !Number.isFinite(last) || last === 0 || Date.now() - last >= COOLDOWN_MS;
        if (fresh) {
          sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
          console.warn('[Recovery] Stale chunk in error boundary — unregister SW + reload');
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) => {
              regs.forEach((r) => r.unregister());
              window.location.reload();
            }).catch(() => window.location.reload());
          } else {
            window.location.reload();
          }
          return; // skip Sentry reporting for an expected stale-deploy hiccup
        }
      } catch {
        /* sessionStorage unavailable — fall through to boundary UI */
      }
    }

    // Report to Sentry if available
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).Sentry) {
      (window as unknown as Record<string, { captureException: (e: Error, opts: unknown) => void }>).Sentry.captureException(error, {
        extra: { componentStack: info.componentStack, screen: this.props.screenName },
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-4" dir="rtl">
          <div className="glass-card p-6 max-w-sm w-full text-center space-y-4">
            <div className="text-4xl">⚠️</div>
            <h3 className="text-lg font-bold text-red-300">משהו השתבש</h3>
            <p className="text-sm text-slate-400">
              {this.props.screenName ? `שגיאה ב${this.props.screenName}` : 'אירעה שגיאה בטעינת המסך'}
            </p>
            {this.state.error && (
              <p className="text-xs text-slate-600 bg-navy-800 rounded-lg p-2 break-words" dir="ltr">
                {this.state.error.message}
              </p>
            )}
            <div className="flex gap-3">
              {this.props.onBack && (
                <button onClick={this.props.onBack} className="btn-secondary flex-1">
                  חזרה
                </button>
              )}
              <button onClick={this.handleRetry} className="btn-primary flex-1">
                נסה שוב
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
